import React, { useState } from 'react';
import { CalendarClock, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from './Button';
import {
    FlatMonthSuggestion,
    findFlatSuggestion,
    flatSuggestionKey,
} from '../services/flatRateBooking';

/**
 * MONATSPAUSCHALE — der Buchungsdialog des Monatsvorschlags.
 *
 * Duenne Komponente, an zwei Stellen eingehaengt (Projekt: ProjectStats;
 * Rechnungsweg: Anlagedialog in InvoicesView): die Regeln liegen in
 * flatRateBooking, geschrieben wird ausschliesslich ueber den `onBook` des
 * Aufrufers (onUpdate-Pfad im Projekt, flatRateBookingService im
 * Rechnungsweg) — der Dialog selbst schreibt nie.
 *
 * Betreiber-Entscheidungen, die hier sichtbar werden:
 *  1. Menge vorbelegt mit 1, FREI ueberschreibbar (0,5 fuer einen
 *     angebrochenen Monat). Der Monat bleibt trotzdem nur einmal buchbar —
 *     der Schluessel ist der Riegel, nicht die Menge.
 *  4. NIE still automatisch: gebucht wird ausschliesslich der eine Monat, den
 *     der Nutzer hier bestaetigt.
 */

interface Props {
    /** Kontextzeile unter der Ueberschrift (Projektname). */
    subtitle?: string;
    /** Die offenen Monate (suggestFlatMonths) — mindestens einer. */
    suggestions: FlatMonthSuggestion[];
    /** Vorauswahl, wenn der Nutzer einen bestimmten Monat angeklickt hat. */
    initialMonthKey?: string;
    /** Bucht GENAU einen Monat; Klartext-Ergebnis fuer die Anzeige. */
    onBook: (
        suggestion: FlatMonthSuggestion,
        quantity: number,
    ) => Promise<{ ok: boolean; message: string }>;
    onClose: () => void;
}

export const FlatMonthBookingDialog: React.FC<Props> = ({
    subtitle,
    suggestions,
    initialMonthKey,
    onBook,
    onClose,
}) => {
    // Auswahl ueber flatServiceId UND monthKey (flatSuggestionKey): der Monat
    // allein ist bei zwei Pauschalpositionen doppelt, und eine Aufloesung nur
    // per monthKey buchte immer die ERSTE Pauschale — egal, welche Zeile der
    // Nutzer gewaehlt hat.
    const [auswahlKey, setAuswahlKey] = useState<string>(() => {
        const hit = suggestions.find((s) => s.monthKey === initialMonthKey);
        const first = hit ?? suggestions[0];
        return first ? flatSuggestionKey(first) : '';
    });
    // Rohtext statt Zahl — dieselbe Regel wie im Buchungsdialog des Projekts
    // (ServiceTrackingModal): ein Zahlenfeld verwirft die Zwischenstufe "0,".
    const [mengeText, setMengeText] = useState('1');
    const [laeuft, setLaeuft] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    const [erfolg, setErfolg] = useState<string | null>(null);

    const gewaehlt = findFlatSuggestion(suggestions, auswahlKey);
    const menge = Number((mengeText || '').trim().replace(',', '.'));
    const mengeOk = Number.isFinite(menge) && menge > 0;
    // Mehrere Pauschalpositionen: der Positionsname gehoert in die Auswahl,
    // sonst ist "07/2026" doppelt und nicht unterscheidbar.
    const mehrerePositionen = new Set(suggestions.map((s) => s.flatServiceId)).size > 1;

    const buchen = async () => {
        if (!gewaehlt || !mengeOk || laeuft) return;
        setLaeuft(true);
        setFehler(null);
        try {
            const ergebnis = await onBook(gewaehlt, menge);
            if (ergebnis.ok) setErfolg(ergebnis.message);
            else setFehler(ergebnis.message);
        } catch (e: any) {
            setFehler(e?.message || 'Die Buchung ist fehlgeschlagen.');
        } finally {
            setLaeuft(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[220] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl animate-scale-in max-h-[90vh] overflow-y-auto">
                <div className="flex items-start gap-4 mb-4">
                    <div className="bg-indigo-50 p-3 rounded-full"><CalendarClock className="w-6 h-6 text-indigo-600" /></div>
                    <div className="min-w-0">
                        <h3 className="font-bold text-lg text-slate-800">Monatspauschale buchen</h3>
                        {subtitle && <p className="text-sm text-slate-600 mt-1 truncate" title={subtitle}>{subtitle}</p>}
                    </div>
                </div>

                {erfolg ? (
                    <>
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 mb-4 flex gap-2">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                            <p className="text-sm text-emerald-900 leading-relaxed">{erfolg}</p>
                        </div>
                        <div className="flex justify-end">
                            <Button variant="primary" onClick={onClose}>Schließen</Button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="space-y-3 mb-4">
                            <div>
                                <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Monat</label>
                                <select value={auswahlKey} onChange={(e) => setAuswahlKey(e.target.value)}
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                                    {suggestions.map((s) => (
                                        <option key={flatSuggestionKey(s)} value={flatSuggestionKey(s)}>
                                            {s.monthLabel}{mehrerePositionen ? ` — ${s.flatName}` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Menge (Monate)</label>
                                <input type="text" inputMode="decimal" value={mengeText}
                                    onChange={(e) => setMengeText(e.target.value)}
                                    className={`w-full border rounded-lg px-3 py-2 text-sm font-mono ${mengeOk ? 'border-slate-300' : 'border-rose-300 bg-rose-50'}`} />
                                <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                                    Vorschlag 1 Monat — frei überschreibbar (z. B. 0,5 für einen
                                    angebrochenen Monat). Der Monat bleibt trotzdem nur einmal buchbar.
                                </p>
                            </div>
                        </div>

                        {fehler && <p className="text-sm text-rose-600 mb-3 whitespace-pre-line">{fehler}</p>}

                        <div className="flex justify-end gap-3">
                            <Button variant="secondary" onClick={onClose} disabled={laeuft}>Abbrechen</Button>
                            <Button variant="primary" onClick={buchen}
                                disabled={laeuft || !gewaehlt || !mengeOk}
                                icon={laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : undefined}>
                                {gewaehlt ? `${gewaehlt.monthLabel} buchen` : 'Buchen'}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
